
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import CalendarView from './CalendarView';
import CalendarSidebar from './CalendarSidebar';
import ScheduleAssistant from './ScheduleAssistant';
import UpcomingEvents from './UpcomingEvents';
import { mockEvents } from './mockCalendarData';
import { CalendarEvent, DateTimeRange, CalendarViewType } from '@/types/calendar';
import AIEventPrompt from './AIEventPrompt';
import { useCalendarVoiceCommands } from './voiceCommands';
import { Button } from '@/components/ui/button';
import { Mic } from 'lucide-react';
import { useVoiceCommands } from '@/contexts/VoiceCommandContext';
import { useToast } from '@/hooks/use-toast';

const CalendarDashboard: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>(mockEvents);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showAssistant, setShowAssistant] = useState<boolean>(false);
  const [showEventDialog, setShowEventDialog] = useState<boolean>(false);
  const [showAIPrompt, setShowAIPrompt] = useState<boolean>(false);
  const [viewType, setViewType] = useState<CalendarViewType>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const { toast } = useToast();
  const { toggleListening } = useVoiceCommands();
  
  // Handler to create a new event for voice commands
  const handleCreateEventFromVoice = (eventDetails: {
    title: string;
    start: Date;
    end: Date;
    description?: string;
    attendees?: string[];
  }) => {
    // Convert simple string array of attendees to the required format for CalendarEvent
    const formattedAttendees = eventDetails.attendees?.map(email => ({ email })) || [];
    
    const newEvent: CalendarEvent = {
      id: `event-${Date.now()}`,
      title: eventDetails.title,
      start: eventDetails.start,
      end: eventDetails.end,
      description: eventDetails.description,
      attendees: formattedAttendees,
      source: 'local',
      calendarId: 'local'
    };
    
    setEvents(prev => [...prev, newEvent]);
    
    toast({
      title: "Event Created",
      description: `Added "${eventDetails.title}" to your calendar`
    });
  };
  
  // Register calendar-specific voice commands with the correct handler
  useCalendarVoiceCommands(handleCreateEventFromVoice);
  
  // Handler for updating events
  const handleEventUpdate = (updatedEvent: CalendarEvent) => {
    setEvents(prev => prev.map(event => 
      event.id === updatedEvent.id ? updatedEvent : event
    ));
  };
  
  // Handler for deleting events
  const handleEventDelete = (eventId: string) => {
    setEvents(prev => prev.filter(event => event.id !== eventId));
    toast({
      title: "Event Deleted",
      description: "The event has been removed from your calendar"
    });
  };
  
  const handleEventClick = (event: CalendarEvent) => {
    console.log('Event clicked:', event);
  };
  
  const handleTimeSlotClick = (start: Date, end: Date) => {
    console.log('Time slot clicked:', { start, end });
    setShowEventDialog(true);
  };
  
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };
  
  const handleViewChange = (newViewType: CalendarViewType) => {
    setViewType(newViewType);
  };
  
  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };
  
  const handleScheduleEvent = (slot: DateTimeRange) => {
    console.log('Scheduling event at:', slot);
    // Create a new event with the selected time slot
    const newEvent: CalendarEvent = {
      id: `event-${Date.now()}`,
      title: "New Event",
      start: slot.start,
      end: slot.end,
      description: "",
      attendees: [],
      source: 'local',
      calendarId: 'local'
    };
    
    setEvents(prev => [...prev, newEvent]);
    
    toast({
      title: "Event Created",
      description: `Added "New Event" to your calendar`
    });
  };
  
  const handleVoicePrompt = () => {
    toggleListening();
    toast({
      title: "Voice Scheduling",
      description: "Say something like 'Schedule a meeting tomorrow at 3pm'"
    });
  };

  // Handler for AI created events - directly accepts CalendarEvent type
  const handleAIEventCreated = (event: CalendarEvent) => {
    setEvents(prev => [...prev, event]);
    toast({
      title: "Event Created",
      description: `Added "${event.title}" to your calendar`
    });
  };
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-3 space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <h2 className="text-2xl font-semibold">
              {format(selectedDate, 'MMMM yyyy')}
            </h2>
            <p className="text-muted-foreground">
              {format(new Date(), 'EEEE, MMMM d')}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button 
              size="sm" 
              onClick={() => setShowAIPrompt(!showAIPrompt)}
              className="flex items-center gap-2"
            >
              {showAIPrompt ? "Hide AI Event Creator" : "Create Event with AI"}
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleVoicePrompt}
              className="flex items-center gap-2"
            >
              <Mic className="h-4 w-4" />
              Voice Scheduling
            </Button>
            
            <Button
              variant={showAssistant ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAssistant(!showAssistant)}
            >
              {showAssistant ? "Hide AI Assistant" : "Show AI Assistant"}
            </Button>
          </div>
        </div>
        
        <CalendarView
          events={events}
          viewType={viewType}
          currentDate={currentDate}
          onViewChange={handleViewChange}
          onDateChange={handleDateChange}
          onEventAdd={handleAIEventCreated}
          onEventUpdate={handleEventUpdate}
          onEventDelete={handleEventDelete}
        />
      </div>

      <div className="lg:col-span-1 space-y-6">
        <CalendarSidebar 
          sources={[
            { id: 'local', name: 'My Calendar', type: 'local', color: 'text-blue-500', isConnected: true, isVisible: true },
            { id: 'google', name: 'Google Calendar', type: 'google', color: 'text-green-500', isConnected: false, isVisible: false },
            { id: 'apple', name: 'Apple Calendar', type: 'apple', color: 'text-red-500', isConnected: false, isVisible: false }
          ]}
          onSourceToggle={(id) => console.log('Toggle source', id)}
          upcomingEvents={events.filter(event => event.start >= new Date())}
        />
        
        <UpcomingEvents 
          events={events.filter(event => event.start >= new Date())}
          onEventUpdate={handleEventUpdate}
          onEventDelete={handleEventDelete}
        />
      </div>
      
      {showAssistant && (
        <div className="lg:col-span-4">
          <div className="rounded-lg border bg-card">
            <ScheduleAssistant
              events={events}
              onEventAdd={handleAIEventCreated}
            />
          </div>
        </div>
      )}
      
      {showAIPrompt && (
        <div className="lg:col-span-4">
          <AIEventPrompt 
            events={events}
            onEventAdd={handleAIEventCreated}
          />
        </div>
      )}
    </div>
  );
};

export default CalendarDashboard;
